import React, { useEffect, useState } from 'react';
import { getUserOrders } from '../../services/order.api';
import { getUserBookings } from '../../services/booking.api';
import { Package, Calendar, CheckCircle, AlertCircle, Link as LinkIcon } from 'lucide-react';
import type { Order } from '../../services/order.api';
import type { Booking } from '../../services/booking.api';

/**
 * DEBUG PAGE - Shows how Orders and Bookings are linked
 * Navigate to /debug/flow to see this page
 */
export function FlowDebugPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ordersData, bookingsData] = await Promise.all([
        getUserOrders(),
        getUserBookings(),
      ]);
      setOrders(ordersData);
      setBookings(bookingsData);
      console.log('📦 Orders:', ordersData);
      console.log('🔧 Bookings:', bookingsData);
    } catch (err: unknown) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold mb-4">Loading data...</h1>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h1 className="text-xl font-bold text-red-800 mb-2">Error Loading Data</h1>
            <p className="text-red-600">{error}</p>
            <p className="text-sm text-red-500 mt-4">
              Make sure the backend is running on http://localhost:5000
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">🔗 Orders & Bookings Flow Debug</h1>
          <p className="text-muted-foreground">
            This page shows how orders and bookings are linked together
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-6">
            <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">{orders.length}</div>
            <div className="text-sm text-blue-600 dark:text-blue-300">Total Orders</div>
          </div>
          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg p-6">
            <div className="text-2xl font-bold text-green-700 dark:text-green-400">{bookings.length}</div>
            <div className="text-sm text-green-600 dark:text-green-300">Total Bookings</div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900 rounded-lg p-6">
            <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">
              {orders.filter(o => o.relatedBookingId).length}
            </div>
            <div className="text-sm text-purple-600 dark:text-purple-300">Linked Orders</div>
          </div>
        </div>

        {/* Connection Diagram */}
        <div className="mb-8 bg-card rounded-lg border p-6">
          <h2 className="text-2xl font-bold mb-6">🔗 Data Connections</h2>
          
          {bookings.map((booking) => {
            const linkedOrders = orders.filter(o => o.relatedBookingId === booking.id);
            
            return (
              <div key={booking.id} className="mb-8 p-6 bg-muted/30 rounded-lg">
                {/* Booking */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">
                      Booking: {booking.serviceType.replace(/_/g, ' ')}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      ID: {booking.id}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Date: {new Date(booking.scheduledDate).toLocaleDateString()}
                    </p>
                    {booking.diagnosisIssue && (
                      <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                        Issue: {booking.diagnosisIssue}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="inline-block px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-sm font-medium">
                      {booking.status}
                    </span>
                  </div>
                </div>

                {/* Related Orders */}
                {linkedOrders.length > 0 ? (
                  <div className="ml-16 space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <LinkIcon className="h-4 w-4" />
                      <span>{linkedOrders.length} Related Order{linkedOrders.length > 1 ? 's' : ''}</span>
                    </div>
                    
                    {linkedOrders.map((order) => (
                      <div key={order.id} className="flex items-center gap-4 p-4 bg-background rounded-lg border">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded flex items-center justify-center">
                          <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{order.productName}</h4>
                          <p className="text-sm text-muted-foreground font-mono">#{order.orderId}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">${order.totalAmount.toFixed(2)}</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            order.status === 'delivered' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            order.status === 'shipped' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ml-16 p-4 bg-background rounded-lg border border-dashed">
                    <p className="text-sm text-muted-foreground">No orders linked to this booking yet</p>
                  </div>
                )}
              </div>
            );
          })}

          {/* Orphan Orders */}
          {orders.filter(o => !o.relatedBookingId).length > 0 && (
            <div className="p-6 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-900">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                Orders Without Booking
              </h3>
              <div className="space-y-2">
                {orders.filter(o => !o.relatedBookingId).map((order) => (
                  <div key={order.id} className="flex items-center gap-4 p-3 bg-background rounded border">
                    <Package className="h-5 w-5 text-amber-600" />
                    <div className="flex-1">
                      <span className="font-medium">{order.productName}</span>
                      <span className="text-sm text-muted-foreground ml-2">#{order.orderId}</span>
                    </div>
                    <span className="text-sm">{order.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Raw Data */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-card rounded-lg border p-6">
            <h3 className="text-xl font-bold mb-4">📦 Orders Raw Data</h3>
            <div className="bg-muted/30 rounded p-4 overflow-auto max-h-96">
              <pre className="text-xs">{JSON.stringify(orders, null, 2)}</pre>
            </div>
          </div>
          
          <div className="bg-card rounded-lg border p-6">
            <h3 className="text-xl font-bold mb-4">🔧 Bookings Raw Data</h3>
            <div className="bg-muted/30 rounded p-4 overflow-auto max-h-96">
              <pre className="text-xs">{JSON.stringify(bookings, null, 2)}</pre>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-6">
          <h3 className="font-bold text-blue-900 dark:text-blue-200 mb-2">✅ What to Check:</h3>
          <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span><strong>relatedBookingId</strong> in orders should match booking <strong>id</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span><strong>relatedOrders[]</strong> in bookings should contain linked order objects</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span><strong>diagnosisIssue</strong> should appear in both orders and bookings</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
